import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MdSearch, MdContactMail, MdAdd, MdVisibility } from 'react-icons/md';
import { useDebouncedSearch } from '@euroasiann/shared';
import { PageSkeleton } from '@euroasiann/shared/ui';
import { Modal } from '../../components/shared/Modal';
import { useToast } from '../../components/shared/Toast';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import { CountryCodeSelect } from '../../components/ui/CountryCodeSelect';
import {
  DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
  displayContactPhoneLine,
} from '../../utils/contact-phone';
import type { OfferCrmContact } from '../Offers/components/OfferCrmSuggestField';

type CrmListResponse = {
  contacts: OfferCrmContact[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
};

const cellLine = 'whitespace-nowrap text-sm tabular-nums';

const EMPTY_FORM = {
  customerOrganizationName: '',
  recipientEmail: '',
  recipientName: '',
  contactName: '',
  contactEmail: '',
  contactPhoneCountryCode: DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
  contactPhone: '',
  contactDepartment: '',
};

export function CrmPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'view'>('add');
  const [form, setForm] = useState(EMPTY_FORM);
  const {
    inputValue: searchQuery,
    submittedValue: submittedSearch,
    setInputValue: setSearchQuery,
    onKeyDown: onSearchKeyDown,
  } = useDebouncedSearch();

  const { data, isLoading, isFetching } = useQuery<CrmListResponse>({
    queryKey: ['admin-crm-contacts', page, submittedSearch],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({
        page: String(page),
        limit: '25',
      });
      if (submittedSearch.trim()) params.set('search', submittedSearch.trim());
      const res = await authenticatedFetch(`/api/v1/admin/crm-contacts?${params.toString()}`, { signal });
      const json = await res.json();
      if (!json?.success) throw new Error(json?.error || 'Failed to load CRM contacts');
      return json.data as CrmListResponse;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: typeof EMPTY_FORM) => {
      const res = await authenticatedFetch('/api/v1/admin/crm-contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || 'Failed to save contact');
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-crm-contacts'] });
      showToast('CRM contact saved', 'success');
      setModalOpen(false);
      setForm(EMPTY_FORM);
    },
    onError: (err: Error) => showToast(err.message, 'error'),
  });

  const contacts = data?.contacts ?? [];
  const pagination = data?.pagination;

  const openAdd = () => {
    setModalMode('add');
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openView = (row: OfferCrmContact) => {
    setModalMode('view');
    setForm({
      customerOrganizationName: row.customerOrganizationName || '',
      recipientEmail: row.recipientEmail || '',
      recipientName: row.recipientName || '',
      contactName: row.contactName || '',
      contactEmail: row.contactEmail || '',
      contactPhoneCountryCode: row.contactPhoneCountryCode || DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
      contactPhone: row.contactPhone || '',
      contactDepartment: row.contactDepartment || '',
    });
    setModalOpen(true);
  };

  const fieldClass =
    'w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm';

  return (
    <div className="p-6 md:p-8 mx-auto space-y-6 max-w-[100%]">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(var(--foreground))] flex items-center gap-2">
            <MdContactMail className="text-[#0088cc]" />
            CRM
          </h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
            Contacts saved from admin offers and quotations. Use them for autocomplete when creating new offers.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <button
            type="button"
            onClick={openAdd}
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#0088cc] text-white text-sm font-semibold hover:bg-[#0077b3]"
          >
            <MdAdd className="w-5 h-5" />
            Add details
          </button>
          <div className="relative w-full sm:w-80">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => {
                setPage(1);
                setSearchQuery(e.target.value);
              }}
              onKeyDown={onSearchKeyDown}
              placeholder="Search org, email, contact…"
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-sm"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <PageSkeleton />
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-[hsl(var(--border))]">
            <table className="w-full min-w-[1400px] border-collapse">
              <thead>
                <tr className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
                  {[
                    'Organization',
                    'Recipient email',
                    'Recipient name',
                    'Contact name',
                    'Contact email',
                    'Phone',
                    'Department',
                    'Offers',
                    '',
                  ].map((h) => (
                    <th
                      key={h || 'actions'}
                      className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[hsl(var(--border))]">
                {contacts.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-[hsl(var(--muted-foreground))]">
                      No CRM contacts yet. Create an offer with customer details or use Add details.
                    </td>
                  </tr>
                ) : (
                  contacts.map((row) => (
                    <tr key={row._id} className="hover:bg-[hsl(var(--muted))]/40">
                      <td className={cn('px-5 py-3', cellLine)}>{row.customerOrganizationName || '—'}</td>
                      <td className={cn('px-5 py-3', cellLine)}>{row.recipientEmail || '—'}</td>
                      <td className={cn('px-5 py-3', cellLine)}>{row.recipientName || '—'}</td>
                      <td className={cn('px-5 py-3', cellLine)}>{row.contactName || '—'}</td>
                      <td className={cn('px-5 py-3', cellLine)}>{row.contactEmail || '—'}</td>
                      <td className={cn('px-5 py-3', cellLine)}>
                        {displayContactPhoneLine(row.contactPhoneCountryCode, row.contactPhone) || '—'}
                      </td>
                      <td className={cn('px-5 py-3', cellLine)}>{row.contactDepartment || '—'}</td>
                      <td className={cn('px-5 py-3 text-center', cellLine)}>{row.offerCount ?? 0}</td>
                      <td className="px-5 py-3 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openView(row)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#0088cc] hover:underline"
                        >
                          <MdVisibility className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-[hsl(var(--muted-foreground))]">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} contacts)
                {isFetching ? ' · Updating…' : ''}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className={cn(
                    'px-3 py-1.5 rounded-md border text-xs font-semibold',
                    page <= 1 ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#0088cc]/10'
                  )}
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= pagination.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={cn(
                    'px-3 py-1.5 rounded-md border text-xs font-semibold',
                    page >= pagination.totalPages ? 'opacity-40 cursor-not-allowed' : 'hover:bg-[#0088cc]/10'
                  )}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalMode === 'add' ? 'Add CRM details' : 'Contact details'}
        size="large"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 space-y-1">
            <label className="text-xs font-semibold">Organization</label>
            <input
              className={fieldClass}
              value={form.customerOrganizationName}
              disabled={modalMode === 'view'}
              onChange={(e) => setForm((f) => ({ ...f, customerOrganizationName: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold">Recipient email</label>
            <input
              type="email"
              className={fieldClass}
              value={form.recipientEmail}
              disabled={modalMode === 'view'}
              onChange={(e) => setForm((f) => ({ ...f, recipientEmail: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold">Recipient name</label>
            <input
              className={fieldClass}
              value={form.recipientName}
              disabled={modalMode === 'view'}
              onChange={(e) => setForm((f) => ({ ...f, recipientName: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold">Contact name</label>
            <input
              className={fieldClass}
              value={form.contactName}
              disabled={modalMode === 'view'}
              onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-semibold">Contact email</label>
            <input
              type="email"
              className={fieldClass}
              value={form.contactEmail}
              disabled={modalMode === 'view'}
              onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
            />
          </div>
          {modalMode === 'view' ? (
            <div className="md:col-span-2 space-y-1">
              <label className="text-xs font-semibold">Phone / WhatsApp</label>
              <input
                className={fieldClass}
                readOnly
                value={displayContactPhoneLine(form.contactPhoneCountryCode, form.contactPhone)}
              />
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <label className="text-xs font-semibold">Phone country code</label>
                <CountryCodeSelect
                  value={form.contactPhoneCountryCode}
                  onChange={(code) => setForm((f) => ({ ...f, contactPhoneCountryCode: code }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold">Phone / WhatsApp</label>
                <input
                  className={fieldClass}
                  value={form.contactPhone}
                  onChange={(e) => setForm((f) => ({ ...f, contactPhone: e.target.value }))}
                />
              </div>
            </>
          )}
          <div className="md:col-span-2 space-y-1">
            <label className="text-xs font-semibold">Department</label>
            <input
              className={fieldClass}
              value={form.contactDepartment}
              disabled={modalMode === 'view'}
              onChange={(e) => setForm((f) => ({ ...f, contactDepartment: e.target.value }))}
            />
          </div>
        </div>
        {modalMode === 'add' && (
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="px-4 py-2 rounded-lg border text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={() => saveMutation.mutate(form)}
              className="px-4 py-2 rounded-lg bg-[#0088cc] text-white text-sm font-semibold disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save contact'}
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
